import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Circle, ClipboardCheck, Loader2 } from 'lucide-react';
import { getBookingDocumentRequirements, type BookingDocumentRequirement } from '../../lib/documents';
import DocumentFillModal from './DocumentFillModal';

interface BookingDocumentsSectionProps {
  businessId: string;
  bookingId: string;
}

/** Shows the documents tied to this booking (via booking procedures) and their status. */
export default function BookingDocumentsSection({ businessId, bookingId }: BookingDocumentsSectionProps) {
  const [reqs, setReqs] = useState<BookingDocumentRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [fillTemplateId, setFillTemplateId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await getBookingDocumentRequirements(businessId, bookingId);
      setReqs(data);
    } finally {
      setLoading(false);
    }
  }, [businessId, bookingId]);

  useEffect(() => { void load(); }, [load]);

  // Nothing configured for this business → render nothing (no empty section).
  if (!loading && reqs.length === 0) return null;

  return (
    <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <ClipboardCheck className="h-4 w-4 text-emerald-600" />
        <h3 className="text-[14px] font-semibold text-gray-900">Documents</h3>
      </div>
      <div className="p-3 space-y-2">
        {loading ? (
          <div className="flex justify-center py-4 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          reqs.map((r) => (
            <div key={r.procedure_id} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
              {r.completed
                ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                : <Circle className="h-5 w-5 text-gray-300 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-gray-900 truncate">{r.title || 'Document'}</div>
                <div className="text-[11px] text-gray-500">
                  {r.facing === 'customer' ? 'Customer sign-off' : 'Staff checklist'}
                  {' · '}
                  {r.enforcement === 'required' ? 'Required to complete booking' : 'Optional'}
                  {r.completed ? ' · Completed' : ''}
                </div>
              </div>
              {!r.completed && (
                <button
                  onClick={() => setFillTemplateId(r.template_id)}
                  className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-semibold hover:bg-blue-700"
                >
                  {r.facing === 'customer' ? 'Sign' : 'Fill'}
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {fillTemplateId && (
        <DocumentFillModal
          templateId={fillTemplateId}
          contextType="booking"
          contextId={bookingId}
          onClose={() => setFillTemplateId(null)}
          onCompleted={() => { setFillTemplateId(null); void load(); }}
        />
      )}
    </div>
  );
}
